import React, { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';

import DesktopModal, { DesktopModalProps } from './desktop-modal';
import { MobileModalWithoutGesturesProps } from './mobile-modal-without-gestures';
import { MobileModalWithGestures, MobileModalWithGesturesProps } from './mobile-modal-with-gestures';
import { useModalBreakPoints } from './use-modal-break-points';
import { cn } from '@restworld/utils-common';

export type BaseModalVariant = 'center' | 'right';

export type StylesProps = {
  modalStyles?: string[];
  headerStyles?: string[];
};

export type BaseModalProps = MobileModalWithGesturesProps &
  MobileModalWithoutGesturesProps &
  DesktopModalProps &
  StylesProps & { footer?: React.ReactNode; disableOverlayClose?: boolean };

export function BaseModal({
  variant = 'center',
  closeIcon,
  dragHandleIcon,
  showBackButton,
  onClose,
  backIcon,
  onBack,
  showMobileGestures = true,
  open,
  title,
  children,
  modalStyles,
  noPadding,
  showDragHandleIcon,
  withTransition = true,
  hideHeader = false,
  showCloseIcon,
  footer,
  footerStyles,
  primaryActionLabel,
  onClickPrimaryAction,
  secondaryActionLabel,
  onClickSecondaryAction,
  padBodyBottom,
  hasHeaderBorderBottom,
  headerStyles,
  CloseIcon,
  disableDrag,
  modalBodyStyles,
  hasOffWhiteBackground,
  disableOverlayClose = false,
}: BaseModalProps) {
  const { isDesktop } = useModalBreakPoints();
  useEffect(() => {
    if (open) {
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';
      document.body.style.overflow = 'visible';
    } else {
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';
    }
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';
    };
  }, [open]);
  const desktopModalEl = (
    <DesktopModal
      {...{
        variant,
        closeIcon,
        showBackButton,
        onClose,
        backIcon,
        onBack,
        open,
        title,
        noPadding,
        modalStyles,
        withTransition,
        hideHeader,
        showCloseIcon,
        footer,
        footerStyles,
        primaryActionLabel,
        onClickPrimaryAction,
        secondaryActionLabel,
        onClickSecondaryAction,
        headerStyles,
        CloseIcon,
        modalBodyStyles,
        hasOffWhiteBackground,
      }}
    >
      {children}
    </DesktopModal>
  );
  const mobileModalWithGestureEl = (
    <MobileModalWithGestures
      {...{
        variant,
        dragHandleIcon,
        showBackButton,
        onClose,
        backIcon,
        onBack,
        showMobileGestures,
        open,
        title,
        children,
        noPadding,
        modalStyles,
        showDragHandleIcon,
        footer,
        footerStyles,
        primaryActionLabel,
        onClickPrimaryAction,
        secondaryActionLabel,
        onClickSecondaryAction,
        padBodyBottom,
        hasHeaderBorderBottom,
        headerStyles,
        disableDrag,
        hasOffWhiteBackground,
      }}
    />
  );
  return (
    // The `key` remounts the whole dialog when the viewport crosses the 768px breakpoint.
    // Without it, swapping desktop <-> mobile unmounts the nested `Transition.Child` that only
    // exists in the desktop branch; headlessui's NestingContext then sees an empty registry,
    // concludes the leave transition is over and marks the whole tree as hidden - while `show`
    // is still true. The root only returns to `visible` on a `show` change, so the modal
    // disappears from the DOM and can never be reopened. See RW-90.
    <Transition
      key={isDesktop ? 'desktop' : 'mobile'}
      appear
      show={open}
      as={Fragment}
      enter="ease-out duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in-out duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <StyledDialog onClose={disableOverlayClose ? () => undefined : onClose} data-cy="base-modal">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Container variant={variant}>
            {isDesktop ? (
              withTransition ? (
                <ModalTransition variant={variant}>{desktopModalEl}</ModalTransition>
              ) : (
                desktopModalEl
              )
            ) : (
              mobileModalWithGestureEl
            )}
          </Container>
        </Transition.Child>
      </StyledDialog>
    </Transition>
  );
}

const StyledDialog = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => (
  <Dialog
    ref={ref}
    className={cn(
      'fixed top-0 left-0 flex justify-end items-start flex-col box-border h-[100dvh] max-h-[100dvh] w-full overflow-hidden z-[100]',
      className
    )}
    {...props}
    onClose={props.onClose ?? (() => undefined)}
  />
));
StyledDialog.displayName = 'StyledDialog';

const Container = React.forwardRef<HTMLDivElement, { variant: BaseModalVariant } & React.HTMLAttributes<HTMLElement>>(
  ({ variant, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex justify-end items-start flex-col box-border bg-black/30 h-[100dvh] max-h-[100dvh] w-full overflow-hidden overflow-y-hidden',
        variant === 'center' && 'lg:items-center md:items-center lg:justify-center md:justify-center',
        variant === 'right' && 'lg:items-end md:items-end',
        className
      )}
      {...props}
    />
  )
);
Container.displayName = 'Container';

interface ModalTransitionProps {
  variant: BaseModalVariant;
  children: React.ReactNode;
}

const ModalTransition = ({ variant, children }: ModalTransitionProps) => {
  return (
    <Transition.Child
      as={Fragment}
      enter={variant === 'right' ? 'transition ease-in-out duration-200 transform' : 'ease-in-out duration-200'}
      enterFrom={variant === 'right' ? 'translate-x-full' : 'opacity-0  scale-0'}
      enterTo={variant === 'right' ? 'translate-x-0' : 'opacity-100 scale-100'}
      leave={variant === 'right' ? 'ease-in-out duration-200' : 'ease-in duration-200'}
      leaveFrom={variant === 'right' ? 'translate-x-0' : 'opacity-100 scale-100'}
      leaveTo={variant === 'right' ? 'translate-x-full' : 'opacity-0 scale-95'}
    >
      {children}
    </Transition.Child>
  );
};
